import { BillingCycle } from './bundle'
import { OrganizationFeatureType } from './organization'

export type BillingServicePricingType = 'block'|'creator'|'activeUser'
export type BillingService = {
  type: 'chargebee'|'freetier',
  subscriptionId: string,
  purchasedLicenses?: number,
  pricingType?: BillingServicePricingType,
  planName?: string,
  maxGroups?: number,
  maxLearners?: number,
  maxTranslatedWords?: number,
  maxGnowbeAi?: number,
  bonusCreditsGnowbeAi?: number,
  planId?: string,
}

/**
 * A workspace on an active-user plan (Pro, Pro+, Enterprise `cu-*-0300-*`, GNB-3307): billed on the people active in it
 * per subscription year, up to a paid user level. The only question the dashboard asks about it; false for every old
 * plan.
 */
export function isActiveUserBilling(org: {billingService?: Pick<BillingService, 'pricingType'>|null}|null|undefined) {
  return org?.billingService?.pricingType === 'activeUser'
}

export function showBillingBanner(billingService: BillingService|null) {
  if (!billingService) return false
  return billingService.pricingType === 'block' || billingService.pricingType === 'creator'
}

/**
 *  - Licenses: for block pricing is counting learners + admins + agents + curators, for new creator pricing is just
 *    counting creators (admins, agents, curators excluding @gnowbe.com users).
 *  - maxLicenses: max licenses you can purchase with current plan. For example Starter plan has max 10 (I think)
 *    meaning if you want to have more then 10 creators you have to update to Professional.
 *  - OrgBillingPlan.licensesIncluded just tells you what is the base for licenses (minimum) on new plans this is 1.
 *    Basically what you get for OrgBillingPlan.price
 *  - OrgBillingPlan.extraLicensesPricePerStep tells you what is the step when you buy new licenses. For new plans this
 *    is 1. And OrgBillingPlan.extraLicensesStep tells you what is price for each step. In new plans, since step is 1,
 *    what is the price for each extra license.
 *  - maxLearners: Max amount of learners organization can have under the plan (of course check only if set).
 *  - addons: Addons are additional features that can be added to the subscription. For example, if you want to have
 *   more users then plan allows you as default.
 */

export type PlanExtraLicensesPricePerStep = {
  price: number,
  startingUnit: number,
  endingUnit?: number,
}
export type AttachedAddon = {
  id: string,
  object: string,
  quantity: number,
  type: string,
}

export type OrgBillingPlan = {
  billingPeriod: BillingCycle,
  description: string,
  extraLicensesPricePerStep: PlanExtraLicensesPricePerStep[],
  extraLicensesStep: number,
  id: string,
  invoiceName: string,
  licensesIncluded: number,
  name: string,
  price: number,
  priceCurrency: string,
  status: string, // "active"|"archived"
  level?: number,
  maxGroups?: number,
  maxLearners?: number,
  maxGnowbeAi?: number,
  maxLicenses?: number,
  shopRevenueFee?: number,
  features?: OrganizationFeatureType[],
  onlyThroughSales: boolean,
  cancelOnlyThroughSales: boolean,
  minLicenses?: number,
  attachedAddons?: AttachedAddon[],
}

export type OrgBillingPaymentSourceCard = {
  billingCountry?: string,
  billingState?: string,
  billingZip?: string,
  brand?: string,
  expiryMonth?: number,
  expiryYear?: number,
  firstName?: string,
  id: string,
  last4?: string,
  lastName?: string,
  maskedNumber?: string,
}
export type OrgBillingStatus = 'pending'|'active'|'cancelled'
export type OrgBillingSubscription = {
  billingPeriod: BillingCycle,
  expiresAt?: number,
  nextPaymentAt?: number,
  organizationId: string,
  plan?: OrgBillingPlan,
  planId: string,
  price: number,
  priceCurrency: string,
  purchasedLicenses: number,
  service: string, // "chargebee"
  startedAt: number,
  status: OrgBillingStatus,
  subscriptionId: string,
  trialExpiresAt?: number,
  paymentSourceCard?: OrgBillingPaymentSourceCard,
  renewalChanges?: {
    planId: string,
    plan?: OrgBillingPlan,
    price: number,
    priceCurrency: string,
    billingPeriod: BillingCycle,
    purchasedLicenses: number,
    addons?: {id: string, quantity: number}[],
  },
  addons?: {id: string, quantity: number}[],
}

export type OrgBillingSubscriptionUpdateEstimate = {
  invoiceNowCurrency?: string,
  invoiceNowTotal?: number,
  renewal: OrgBillingSubscription,
}

export const getOrgBillingPlanFragmentName = () => {
  return 'OrgBillingPlanFields'
}
export const getOrgBillingPlanFragment = () => `
  fragment ${getOrgBillingPlanFragmentName()} on OrgBillingPlan {
    billingPeriod
    description
    extraLicensesPricePerStep {
      price
      startingUnit
      endingUnit
    }
    extraLicensesStep
    id
    invoiceName
    licensesIncluded
    name
    price
    priceCurrency
    status
    level
    shopRevenueFee
    features
    cancelOnlyThroughSales
    onlyThroughSales
    maxGroups
    maxLearners
    maxGnowbeAi
    maxLicenses
    minLicenses
    attachedAddons {
      id
      object
      quantity
      type
    }
  }
`
export const getOrgBillingSubscriptionFragmentName = () => {
  return 'OrgBillingSubscriptionFields'
}
export const getOrgBillingSubscriptionFragment = () => `
  ${getOrgBillingPlanFragment()}
  fragment ${getOrgBillingSubscriptionFragmentName()} on OrgBillingSubscription {
    billingPeriod
    expiresAt
    nextPaymentAt
    organizationId
    plan {
      ...${getOrgBillingPlanFragmentName()}
    }
    planId
    price
    priceCurrency
    purchasedLicenses
    service
    startedAt
    status
    subscriptionId
    trialExpiresAt
    paymentSourceCard {
      billingCountry
      billingState
      billingZip
      brand
      expiryMonth
      expiryYear
      firstName
      id
      last4
      lastName
      maskedNumber
    }
    renewalChanges {
      planId
      plan {
        ...${getOrgBillingPlanFragmentName()}
      }
      price
      priceCurrency
      billingPeriod
      purchasedLicenses
      addons {
        id
        quantity
      }
    }
    addons {
      id
      quantity
    }
  }
`

export type PlanAddon = {
  chargeType?: string,
  id : string,
  metaData: object
  name: string,
  period?: number,
  periodUnit: string,
  status: string,
  tiers?: {
    ending_unit?: number,
    object?: string,
    price?: number,
    starting_unit?: number,
  }[],
  type: string,
}
